import type {
  CapitalCall,
  CapitalReturn,
  ChangeRequest,
  CommunicationLog,
  Distribution,
  Holder,
  InvestorTask,
  LedgerEntry,
  PaymentException,
  RedemptionLine,
  RedemptionRun,
  RegistryDocument,
  WithholdingRemittance,
} from '../types';
import { FUNDS } from './seedReference';

const fundCode = (fundId: string) => FUNDS.find((fund) => fund.id === fundId)?.code ?? fundId;

function unitsHeld(ledger: LedgerEntry[], holderId: string, classId: string) {
  return ledger
    .filter((entry) => entry.holderId === holderId && entry.classId === classId)
    .reduce((total, entry) => total + entry.units, 0);
}

export function buildRedemptionRuns(
  pastLines: RedemptionLine[],
  ledger: LedgerEntry[],
  holders: Holder[],
  changeRequests: ChangeRequest[],
): { runs: RedemptionRun[]; changeRequests: ChangeRequest[] } {
  const pastIds = new Set(pastLines.map((line) => line.holderId));
  const candidates = holders
    .filter(
      (holder) =>
        holder.tenantId === 't-fides' && !holder.investorLoginId && !pastIds.has(holder.id),
    )
    .filter((holder) => unitsHeld(ledger, holder.id, 'c-core-a') > 0)
    .slice(0, 3);
  const queued: ChangeRequest[] = candidates.map((holder, index) => {
    const units = Math.round(unitsHeld(ledger, holder.id, 'c-core-a') * [0.25, 0.5, 1][index]);
    return {
      id: index === 0 ? 'cr-4' : `cr-q${index}`,
      reference: index === 0 ? 'CR-26-0315' : `CR-26-03${20 + index}`,
      holderId: holder.id,
      kind: 'REDEMPTION',
      status: 'QUEUED',
      channel: 'INVESTOR_PORTAL',
      submittedAt: `2026-10-${String(9 + index * 2).padStart(2, '0')}`,
      fundId: 'f-core',
      classId: 'c-core-a',
      units,
      details: [
        { label: 'Redemption window', value: 'Quarterly, 31 Dec 2026' },
        { label: 'Price', value: 'NAV at window date' },
      ],
      verification: ['MFA step-up passed', 'Bank account verified'],
    };
  });
  const merged = [...changeRequests.filter((request) => request.id !== 'cr-4'), ...queued];
  const runs: RedemptionRun[] = [
    {
      id: 'rr-core-dec',
      fundId: 'f-core',
      classId: 'c-core-a',
      windowDate: '2026-12-31',
      cutoffDate: '2026-12-15',
      gatePct: 5,
      status: 'OPEN',
      lines: queued.map((request) => ({
        holderId: request.holderId,
        requestId: request.id,
        unitsRequested: request.units ?? 0,
        unitsRedeemed: 0,
        amount: 0,
      })),
    },
    {
      id: 'rr-core-sep',
      fundId: 'f-core',
      classId: 'c-core-a',
      windowDate: '2026-09-30',
      cutoffDate: '2026-09-15',
      gatePct: 10,
      unitPrice: pastLines[0] ? pastLines[0].amount / pastLines[0].unitsRedeemed : undefined,
      status: 'PAID',
      approvedBy: 'Priya Nair',
      lines: pastLines,
    },
  ];
  return { runs, changeRequests: merged };
}

export function buildPaymentExceptions(
  holders: Holder[],
  distributions: Distribution[],
): PaymentException[] {
  const exceptions: PaymentException[] = [];
  const september = distributions.filter(
    (item) => item.status === 'PAID' && item.paymentDate === '2026-10-10',
  );
  for (const distribution of september) {
    for (const line of distribution.lines) {
      const holder = holders.find((item) => item.id === line.holderId);
      if (holder && !holder.bank.verified && line.method === 'CASH') {
        exceptions.push({
          id: `pe-held-${distribution.id}-${holder.id}`,
          fundId: distribution.fundId,
          holderId: holder.id,
          eventLabel: `${fundCode(distribution.fundId)} distribution, period ending ${distribution.periodEnd}`,
          amount: line.net,
          reason: 'HELD_UNVERIFIED_BANK',
          raisedAt: distribution.paymentDate,
          status: 'AWAITING_INVESTOR',
        });
      }
    }
  }
  const august = distributions.find((item) => item.id === 'ds-psd-a-08');
  august?.lines.slice(5, 7).forEach((line, index) => {
    exceptions.push({
      id: `pe-ret-${index}`,
      fundId: august.fundId,
      holderId: line.holderId,
      eventLabel: `PSDF distribution, period ending ${august.periodEnd}`,
      amount: line.net,
      reason: index === 0 ? 'RETURNED_ACCOUNT_CLOSED' : 'RETURNED_INVALID_ACCOUNT',
      raisedAt: '2026-09-12',
      status: index === 0 ? 'AWAITING_INVESTOR' : 'OPEN',
    });
  });
  const legacy = holders.find(
    (holder) =>
      holder.tenantId === 't-fides' && holder.type === 'INDIVIDUAL' && !holder.investorLoginId,
  );
  if (legacy) {
    exceptions.push({
      id: 'pe-legacy',
      fundId: 'f-psd',
      holderId: legacy.id,
      eventLabel: 'PSDF distribution, Mar 2024 (migrated from Registry Direct)',
      amount: 1_843.12,
      reason: 'RETURNED_ACCOUNT_CLOSED',
      raisedAt: '2024-04-12',
      status: 'OPEN',
    });
  }
  return exceptions;
}

export const REMITTANCES: WithholdingRemittance[] = [
  {
    id: 'rm-07',
    month: '2026-07',
    dueDate: '2026-08-21',
    remittedAt: '2026-08-19',
    reference: 'IAS-JUL-26',
  },
  {
    id: 'rm-08',
    month: '2026-08',
    dueDate: '2026-09-21',
    remittedAt: '2026-09-18',
    reference: 'IAS-AUG-26',
  },
  { id: 'rm-09', month: '2026-09', dueDate: '2026-10-21' },
  { id: 'rm-10', month: '2026-10', dueDate: '2026-11-21' },
];

export function buildExceptionTasks(exceptions: PaymentException[]): InvestorTask[] {
  return exceptions
    .filter((item) => item.status === 'AWAITING_INVESTOR')
    .map((item) => ({
      id: `t-bank-${item.id}`,
      holderId: item.holderId,
      kind: 'CONFIRM_BANK' as const,
      title: 'Confirm your bank account',
      detail: `We couldn't pay ${item.eventLabel.toLowerCase()} (${item.amount.toLocaleString('en-AU', { style: 'currency', currency: 'AUD' })}). Confirm your bank details and we'll pay it straight away.`,
      status: 'OPEN' as const,
      refId: item.id,
    }));
}

export function buildLifecycleDocuments(capitalReturns: CapitalReturn[]): RegistryDocument[] {
  return capitalReturns.flatMap((capitalReturn) =>
    capitalReturn.lines.flatMap((line) => [
      {
        id: `doc-cra-${capitalReturn.id}-${line.holderId}`,
        holderId: line.holderId,
        fundId: capitalReturn.fundId,
        kind: 'CAPITAL_RETURN_ADVICE' as const,
        name: `${fundCode(capitalReturn.fundId)} capital return advice`,
        date: capitalReturn.paymentDate,
        sizeKb: 92,
        visibleToInvestor: true,
        refId: capitalReturn.id,
      },
      ...(capitalReturn.kind === 'FINAL'
        ? [
            {
              id: `doc-exit-${capitalReturn.id}-${line.holderId}`,
              holderId: line.holderId,
              fundId: capitalReturn.fundId,
              kind: 'EXIT_STATEMENT' as const,
              name: `${fundCode(capitalReturn.fundId)} exit statement`,
              date: capitalReturn.paymentDate,
              sizeKb: 118,
              visibleToInvestor: true,
              refId: capitalReturn.id,
            },
          ]
        : []),
    ]),
  );
}

export function buildCommunications(
  distributions: Distribution[],
  calls: CapitalCall[],
  capitalReturns: CapitalReturn[],
  holders: Holder[],
): CommunicationLog[] {
  const fides = holders
    .filter((holder) => holder.tenantId === 't-fides')
    .map((holder) => holder.id);
  const stats = (count: number, seed: number) => ({
    delivered: count,
    opened: Math.round(count * (0.55 + (seed % 5) * 0.06)),
    bounced: count > 20 ? 1 : 0,
  });
  const logs: CommunicationLog[] = distributions
    .filter((item) => item.status === 'PAID' && item.fundId !== 'f-hdcf')
    .map((item, index) => ({
      id: `cm-d-${item.id}`,
      at: `${item.paymentDate}T09:05:00`,
      template: 'DISTRIBUTION_ADVICE',
      subject: `Your ${fundCode(item.fundId)} distribution has been paid`,
      audience: `${fundCode(item.fundId)} holders`,
      fundId: item.fundId,
      holderIds: item.lines.map((line) => line.holderId),
      channels: ['EMAIL', 'PUSH'],
      ...stats(item.lines.length, index),
      sentBy: 'Alex Morgan',
    }));
  const call = calls.find((item) => item.id === 'cc-opp-3');
  if (call) {
    const holderIds = call.lines.map((line) => line.holderId);
    logs.push({
      id: 'cm-call3',
      at: '2026-09-22T10:00:00',
      template: 'CALL_NOTICE',
      subject: 'Capital call notice: OPP1 Call 3',
      audience: 'OPP1 holders',
      fundId: 'f-opp',
      holderIds,
      channels: ['EMAIL', 'PUSH'],
      ...stats(holderIds.length, 2),
      sentBy: 'Priya Nair',
    });
    logs.push({
      id: 'cm-call3-r',
      at: '2026-10-06T09:00:00',
      template: 'CALL_REMINDER',
      subject: 'Reminder: OPP1 Call 3 is due 13 October',
      audience: 'OPP1 holders yet to pay',
      fundId: 'f-opp',
      holderIds: call.lines.filter((line) => line.status !== 'PAID').map((line) => line.holderId),
      channels: ['EMAIL', 'PUSH'],
      ...stats(14, 3),
      sentBy: 'Alex Morgan',
    });
  }
  for (const capitalReturn of capitalReturns) {
    logs.push({
      id: `cm-cr-${capitalReturn.id}`,
      at: `${capitalReturn.paymentDate}T11:00:00`,
      template: 'WIND_UP_NOTICE',
      subject: `${fundCode(capitalReturn.fundId)} has repaid: your capital has been returned`,
      audience: `${fundCode(capitalReturn.fundId)} holders`,
      fundId: capitalReturn.fundId,
      holderIds: capitalReturn.lines.map((line) => line.holderId),
      channels: ['EMAIL', 'PUSH'],
      ...stats(capitalReturn.lines.length, 4),
      sentBy: 'Priya Nair',
    });
  }
  logs.push({
    id: 'cm-tax26',
    at: '2026-09-15T08:30:00',
    template: 'TAX_STATEMENT_READY',
    subject: 'Your FY2026 tax statements are ready',
    audience: 'All holders',
    holderIds: fides,
    channels: ['EMAIL', 'PUSH', 'POST'],
    ...stats(fides.length, 1),
    sentBy: 'Alex Morgan',
  });
  logs.push({
    id: 'cm-ws',
    at: '2026-10-01T08:00:00',
    template: 'WHOLESALE_RENEWAL',
    subject: 'Your wholesale investor certificate is due for renewal',
    audience: 'Holders with certificates expiring by 31 Dec',
    holderIds: ['h-bennett-ind', 'h-bennett-smsf'],
    channels: ['EMAIL', 'PUSH'],
    delivered: 9,
    opened: 6,
    bounced: 0,
    sentBy: 'Registry rules',
  });
  logs.push({
    id: 'cm-qu',
    at: '2026-10-02T12:00:00',
    template: 'GENERAL_UPDATE',
    subject: 'September quarter investor update',
    audience: 'All holders',
    holderIds: fides,
    channels: ['EMAIL', 'PUSH'],
    ...stats(fides.length, 0),
    sentBy: 'Priya Nair',
  });
  return logs.sort((left, right) => right.at.localeCompare(left.at));
}
